import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import ConfirmModal from '../components/common/ConfirmModal';

const BASE = process.env.REACT_APP_API_URL || '';
const api  = (path, opts = {}) =>
  fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}` },
    ...opts,
  }).then(r => r.json());

function toast(msg, ok = true) {
  const el = document.createElement('div');
  el.textContent = msg;
  Object.assign(el.style, {
    position:'fixed', bottom:24, right:24, zIndex:9999,
    padding:'10px 18px', borderRadius:8, fontWeight:600, fontSize:'0.85rem',
    background: ok ? '#22c55e' : '#ef4444', color:'#fff',
    boxShadow:'0 4px 16px rgba(0,0,0,0.2)',
  });
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 3500);
}

function UsageBar({ value, max, color = '#0d9488' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, height:6, borderRadius:3, background:'var(--border)', overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:3, transition:'width 0.3s' }} />
      </div>
      <span style={{ fontSize:'0.78rem', fontFamily:'var(--font-mono)', color:'var(--text-muted)', minWidth:36, textAlign:'right' }}>
        {value.toLocaleString()}
      </span>
    </div>
  );
}

// ── Google Maps Card ──────────────────────────────────────────────────────────
function GoogleMapsCard() {
  const [tab,       setTab]       = useState('config');
  const [keyData,   setKeyData]   = useState(null);
  const [usage,     setUsage]     = useState(null);
  const [days,      setDays]      = useState(30);
  const [loading,   setLoading]   = useState(true);
  const [loadingU,  setLoadingU]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [testing,   setTesting]   = useState(false);
  const [testRes,   setTestRes]   = useState(null);
  const [newKey,    setNewKey]    = useState('');
  const [isActive,  setIsActive]  = useState(true);
  const [editing,   setEditing]   = useState(false);

  const loadKey = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('/admin/google-api');
      setKeyData(d);
      setIsActive(d.is_active ?? true);
    } catch {}
    finally { setLoading(false); }
  }, []);

  const loadUsage = useCallback(async () => {
    setLoadingU(true);
    try { setUsage(await api(`/admin/api-usage?days=${days}&service=google_maps`)); }
    catch {}
    finally { setLoadingU(false); }
  }, [days]);

  useEffect(() => { loadKey(); }, [loadKey]);
  useEffect(() => { if (tab === 'usage') loadUsage(); }, [tab, loadUsage]);

  const saveKey = async () => {
    setSaving(true);
    try {
      const body = { is_active: isActive };
      if (editing && newKey.trim()) body.api_key = newKey.trim();
      const r = await api('/admin/google-api', { method:'PUT', body: JSON.stringify(body) });
      if (r.error) throw new Error(r.error);
      toast('Google Maps key saved');
      setEditing(false); setNewKey('');
      loadKey();
    } catch (e) { toast(e.message || 'Save failed', false); }
    finally { setSaving(false); }
  };

  const testConn = async () => {
    setTesting(true); setTestRes(null);
    try {
      const r = await api('/admin/google-api/test', { method:'POST' });
      setTestRes(r);
      toast(r.ok ? 'Connection successful' : r.error || 'Test failed', r.ok);
    } catch (e) { setTestRes({ ok:false, error:e.message }); }
    finally { setTesting(false); }
  };

  // Daily chart
  const dailyData = (() => {
    if (!usage?.raw?.length) return [];
    const byDate = {};
    usage.raw.forEach(r => { byDate[r.date] = (byDate[r.date] || 0) + r.call_count; });
    const out = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      out.push({ date:key, label: d.toLocaleDateString('en-US',{month:'short',day:'numeric'}), count: byDate[key]||0 });
    }
    return out;
  })();
  const maxDaily  = Math.max(...dailyData.map(d => d.count), 1);
  const maxUsage  = usage?.orgs?.length ? Math.max(...usage.orgs.map(o => o.total)) : 1;

  const tabStyle = (id) => ({
    padding:'8px 16px', background:'none', border:'none', cursor:'pointer',
    fontWeight: tab===id ? 700 : 400, fontSize:'0.85rem', fontFamily:'inherit',
    color: tab===id ? 'var(--gold)' : 'var(--text-muted)',
    borderBottom: tab===id ? '2px solid var(--gold)' : '2px solid transparent',
    marginBottom:-1, transition:'all 0.12s',
  });

  return (
    <div className="card">
      {/* Header */}
      <div className="card-header">
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{
            width:38, height:38, borderRadius:8, background:'rgba(13,148,136,0.12)',
            border:'1px solid rgba(13,148,136,0.25)', display:'flex', alignItems:'center',
            justifyContent:'center', fontFamily:'var(--font-mono)', fontWeight:800, fontSize:'0.75rem', color:'#0d9488',
          }}>GM</div>
          <div>
            <div style={{ fontWeight:700, fontSize:'0.95rem', color:'var(--text-primary)' }}>Google Maps / Places API</div>
            <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>
              Address autocomplete in intake forms and Provider Network. Shared key, access gated per plan.
            </div>
          </div>
        </div>
        {keyData && (
          <span style={{
            padding:'3px 10px', borderRadius:10, fontSize:'0.68rem', fontWeight:700,
            background: keyData.has_key && keyData.is_active ? 'var(--green-dim)' : 'rgba(255,255,255,0.05)',
            color: keyData.has_key && keyData.is_active ? 'var(--green)' : 'var(--text-muted)',
          }}>
            {keyData.has_key && keyData.is_active ? 'Active' : keyData.has_key ? 'Inactive' : 'Not Configured'}
          </span>
        )}
      </div>

      {/* Sub-tabs */}
      <div style={{ display:'flex', borderBottom:'1px solid var(--border)', paddingLeft:20 }}>
        {[['config','Configuration'],['usage','Usage'],['plans','Plan Control']].map(([id,lbl]) => (
          <button key={id} style={tabStyle(id)} onClick={() => setTab(id)}>{lbl}</button>
        ))}
      </div>

      <div className="card-body">
        {loading ? (
          <div style={{ textAlign:'center', padding:30, color:'var(--text-muted)' }}>Loading…</div>
        ) : tab === 'config' ? (
          <div style={{ display:'flex', flexDirection:'column', gap:14, maxWidth:580 }}>
            {/* Status row */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {[
                { label:'API Key', val: keyData?.has_key ? (keyData.api_key_masked || '••••••••••••') : 'Not configured', mono:true },
                { label:'Status',  val: isActive ? 'Enabled' : 'Disabled', color: isActive ? 'var(--green)' : 'var(--text-muted)' },
              ].map(({ label, val, mono, color }) => (
                <div key={label} style={{ padding:'10px 14px', background:'var(--bg-base)', borderRadius:8, border:'1px solid var(--border)' }}>
                  <div style={{ fontSize:'0.68rem', color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>{label}</div>
                  <div style={{ fontWeight:700, fontSize:'0.88rem', color: color||'var(--text-primary)', fontFamily: mono ? 'monospace' : 'inherit' }}>{val}</div>
                </div>
              ))}
            </div>

            {/* Active toggle */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'var(--bg-base)', borderRadius:8, border:'1px solid var(--border)' }}>
              <span style={{ fontSize:'0.84rem', color:'var(--text-muted)', fontWeight:600 }}>Active</span>
              <div onClick={() => setIsActive(v => !v)} style={{ width:40, height:22, borderRadius:11, position:'relative', cursor:'pointer', background: isActive ? '#0d9488' : 'var(--border)', transition:'background 0.2s' }}>
                <div style={{ position:'absolute', top:2, width:18, height:18, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,0.2)', transition:'left 0.18s', left: isActive ? 20 : 2 }} />
              </div>
            </div>
            {isActive !== keyData?.is_active && !editing && (
              <button className="btn btn-success btn-sm" onClick={saveKey} disabled={saving}>
                {saving ? 'Saving…' : 'Save Status Change'}
              </button>
            )}

            {/* Key editor */}
            <div style={{ borderTop:'1px solid var(--border)', paddingTop:14 }}>
              <div style={{ fontSize:'0.84rem', color:'var(--text-muted)', marginBottom:10, lineHeight:1.5 }}>
                Get your API key from{' '}
                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" style={{ color:'var(--gold)', textDecoration:'underline' }}>
                  Google Cloud Console
                </a>. Enable <strong>Maps JavaScript API</strong> and <strong>Places API</strong>.
              </div>
              {!editing ? (
                <button className="btn btn-primary btn-sm" onClick={() => setEditing(true)}>
                  {keyData?.has_key ? 'Replace Key' : 'Set API Key'}
                </button>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <input type="password" className="form-input" autoFocus value={newKey}
                    onChange={e => setNewKey(e.target.value)} placeholder="AIza…" />
                  <div style={{ display:'flex', gap:8 }}>
                    <button className="btn btn-primary btn-sm" onClick={saveKey} disabled={saving || !newKey.trim()}>
                      {saving ? 'Saving…' : 'Save Key'}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(false); setNewKey(''); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>

            {/* Test */}
            {keyData?.has_key && (
              <div style={{ borderTop:'1px solid var(--border)', paddingTop:14 }}>
                <div style={{ fontWeight:700, fontSize:'0.88rem', color:'var(--text-primary)', marginBottom:8 }}>Test Connection</div>
                <button className="btn btn-ghost btn-sm" onClick={testConn} disabled={testing || !isActive}>
                  {testing ? (
                    <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ display:'inline-block', width:12, height:12, border:'2px solid var(--border)', borderTopColor:'var(--gold)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
                      Testing…
                    </span>
                  ) : 'Test Connection'}
                </button>
                {testRes && (
                  <div style={{ marginTop:10, padding:'10px 14px', borderRadius:8,
                    background: testRes.ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                    border:`1px solid ${testRes.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
                    <div style={{ fontWeight:700, fontSize:'0.88rem', color: testRes.ok ? 'var(--green)' : 'var(--red)', marginBottom: testRes.message||testRes.error ? 4 : 0 }}>
                      {testRes.ok ? 'Connection Successful' : 'Connection Failed'}
                    </div>
                    {(testRes.message || testRes.error) && (
                      <div style={{ fontSize:'0.82rem', color:'var(--text-secondary)', lineHeight:1.5 }}>
                        {testRes.ok ? testRes.message : testRes.error}
                      </div>
                    )}
                    {testRes.ok && testRes.predictions?.length > 0 && (
                      <div style={{ marginTop:8 }}>
                        {testRes.predictions.map((p,i) => (
                          <div key={i} style={{ fontSize:'0.78rem', color:'var(--text-muted)', padding:'3px 8px', background:'var(--bg-base)', borderRadius:5, marginTop:3, fontFamily:'monospace' }}>{p}</div>
                        ))}
                      </div>
                    )}
                    {!testRes.ok && (
                      <ul style={{ margin:'8px 0 0 16px', padding:0, fontSize:'0.78rem', color:'var(--text-muted)', lineHeight:1.7 }}>
                        <li>Verify the key in Google Cloud Console</li>
                        <li>Ensure Places API is enabled</li>
                        <li>Check no HTTP referrer restrictions block admin.zeuscms.com</li>
                        <li>Confirm billing is active on the project</li>
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
            <div style={{ padding:'10px 14px', borderRadius:8, background:'var(--blue-dim)', border:'1px solid rgba(59,130,246,0.25)', fontSize:'0.8rem', color:'var(--blue)', lineHeight:1.5 }}>
              This key is shared across all organizations. Access is gated by each org's plan feature flag.
            </div>
          </div>
        ) : tab === 'usage' ? (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {[7,14,30,90].map(d => (
                <button key={d} onClick={() => setDays(d)} style={{
                  padding:'5px 12px', borderRadius:20, cursor:'pointer', fontWeight: days===d ? 700 : 400,
                  fontSize:'0.82rem', fontFamily:'inherit',
                  background: days===d ? 'var(--gold)' : 'var(--bg-card)',
                  color: days===d ? '#0c1628' : 'var(--text-muted)',
                  border:`1px solid ${days===d ? 'var(--gold)' : 'var(--border)'}`,
                }}>Last {d}d</button>
              ))}
            </div>
            {loadingU ? <div style={{ textAlign:'center', padding:30, color:'var(--text-muted)' }}>Loading…</div> : (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', gap:10 }}>
                  {[
                    { label:'Total Calls', val:(usage?.total||0).toLocaleString() },
                    { label:'Active Orgs', val:usage?.orgs?.length||0, color:'var(--blue)' },
                    { label:'Daily Avg', val: usage?.total ? Math.round(usage.total/days) : 0, color:'var(--purple)' },
                  ].map(({ label, val, color }) => (
                    <div key={label} style={{ padding:'12px 14px', background:'var(--bg-base)', borderRadius:8, border:'1px solid var(--border)' }}>
                      <div style={{ fontSize:'0.65rem', color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>{label}</div>
                      <div style={{ fontSize:'1.4rem', fontWeight:800, color: color||'var(--gold)', lineHeight:1 }}>{val}</div>
                    </div>
                  ))}
                </div>
                {dailyData.length > 0 && (
                  <div style={{ background:'var(--bg-base)', border:'1px solid var(--border)', borderRadius:8, padding:'14px 16px' }}>
                    <div style={{ fontWeight:700, fontSize:'0.85rem', color:'var(--text-primary)', marginBottom:12 }}>Daily (Last 14 Days)</div>
                    <div style={{ display:'flex', alignItems:'flex-end', gap:3, height:60 }}>
                      {dailyData.map(d => {
                        const h = maxDaily > 0 ? Math.max(3, Math.round((d.count/maxDaily)*52)) : 3;
                        return (
                          <div key={d.date} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                            <div title={`${d.label}: ${d.count}`} style={{ width:'100%', height:h, borderRadius:'2px 2px 0 0', background: d.count>0 ? '#0d9488' : 'var(--border)' }} />
                            <span style={{ fontSize:'0.5rem', color:'var(--text-muted)', transform:'rotate(-35deg)', transformOrigin:'center', whiteSpace:'nowrap' }}>{d.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {usage?.orgs?.length > 0 && (
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr style={{ background:'var(--bg-base)' }}>
                      <th style={{ padding:'8px 14px', textAlign:'left', fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', color:'var(--text-muted)' }}>Organization</th>
                      <th style={{ padding:'8px 14px', textAlign:'right', fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', color:'var(--text-muted)', width:70 }}>Calls</th>
                      <th style={{ padding:'8px 14px', fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', color:'var(--text-muted)', width:180 }}>Usage</th>
                    </tr></thead>
                    <tbody>{usage.orgs.map((o,i) => (
                      <tr key={o.org_id} style={{ borderTop:'1px solid var(--border)', background: i%2===0 ? 'transparent' : 'var(--bg-base)' }}>
                        <td style={{ padding:'9px 14px', fontSize:'0.85rem', fontWeight:600 }}>{o.name||o.org_id}</td>
                        <td style={{ padding:'9px 14px', textAlign:'right', fontFamily:'var(--font-mono)', color:'var(--gold)', fontWeight:700 }}>{o.total.toLocaleString()}</td>
                        <td style={{ padding:'9px 14px' }}><UsageBar value={o.total} max={maxUsage} /></td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
                {!usage?.orgs?.length && <div style={{ textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem', padding:20 }}>No usage recorded in this period.</div>}
              </>
            )}
          </div>
        ) : (
          <div style={{ fontSize:'0.85rem', color:'var(--text-secondary)', lineHeight:1.6, maxWidth:500 }}>
            The <strong>Google Maps / Places</strong> feature flag controls address autocomplete per org based on their plan. Toggle it on the <strong>Plans</strong> page via the Edit drawer.
            <br /><br />
            <a href="/plans" style={{ color:'var(--gold)', fontWeight:700 }}>Go to Plans page →</a>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Fax.plus / RingCentral Card ───────────────────────────────────────────────
const SERVICES = [
  {
    key:'fax_plus', name:'Fax.plus', abbr:'FX', color:'#0d9488',
    desc:'Cloud fax service — send/receive faxes from case documents.',
    docsUrl:'https://apidoc.fax.plus', setupUrl:'https://app.fax.plus/settings/api',
    fields:[
      { key:'api_key',    label:'API Key',    type:'password', placeholder:'Your Fax.plus API key' },
      { key:'account_id', label:'Account ID', type:'text',     placeholder:'Your account ID' },
    ],
  },
  {
    key:'ringcentral', name:'RingCentral', abbr:'RC', color:'#0ea5e9',
    desc:'Unified communications — fax, voice, SMS from case detail.',
    docsUrl:'https://developers.ringcentral.com', setupUrl:'https://developers.ringcentral.com/my-account.html',
    fields:[
      { key:'client_id',     label:'Client ID',     type:'password', placeholder:'OAuth Client ID' },
      { key:'client_secret', label:'Client Secret', type:'password', placeholder:'OAuth Client Secret' },
      { key:'account_id',    label:'Account ID',    type:'text',     placeholder:'RingCentral Account ID' },
    ],
  },
];

function ServiceCard({ svc }) {
  const [cfg,     setCfg]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);
  const [active,  setActive]  = useState(true);
  const [form,    setForm]    = useState(Object.fromEntries(svc.fields.map(f => [f.key,''])));
  const [confirmAction, setConfirmAction] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await api(`/admin/integrations/${svc.key}`); setCfg(d); setActive(d?.is_active !== false); }
    catch { setCfg(null); }
    finally { setLoading(false); }
  }, [svc.key]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api(`/admin/integrations/${svc.key}`, { method:'PUT', body: JSON.stringify({ ...form, is_active: active }) });
      toast(`${svc.name} saved`);
      setForm(Object.fromEntries(svc.fields.map(f => [f.key,''])));
      setTestRes(null);
      await load();
    } catch { toast('Save failed', false); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestRes(null);
    try {
      const r = await api(`/admin/integrations/${svc.key}/test`, { method:'POST' });
      setTestRes(r);
      toast(r.ok ? `${svc.name} connected` : r.error || 'Test failed', r.ok);
    } catch (e) { setTestRes({ ok:false, error:e.message }); }
    finally { setTesting(false); }
  };

  const disconnect = async () => {
    setConfirmAction({
      title: 'Remove Integration',
      message: `Remove ${svc.name} credentials? This cannot be undone.`,
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        try {
          await api(`/admin/integrations/${svc.key}`, { method:'DELETE' });
          toast('Integration removed'); setTestRes(null); await load();
        } catch { toast('Failed', false); }
      },
    });
  };

  return (
    <div className="card">
      <div className="card-header">
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{
            width:38, height:38, borderRadius:8, background:`${svc.color}18`,
            border:`1px solid ${svc.color}30`, display:'flex', alignItems:'center', justifyContent:'center',
            fontFamily:'var(--font-mono)', fontWeight:800, fontSize:'0.75rem', color:svc.color,
          }}>{svc.abbr}</div>
          <div>
            <div style={{ fontWeight:700, fontSize:'0.95rem', color:'var(--text-primary)' }}>{svc.name}</div>
            <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>{svc.desc}</div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {cfg?.configured && (
            <span style={{
              padding:'2px 10px', borderRadius:10, fontSize:'0.68rem', fontWeight:700,
              background: active ? 'var(--green-dim)' : 'rgba(255,255,255,0.05)',
              color: active ? 'var(--green)' : 'var(--text-muted)',
            }}>{active ? 'Active' : 'Inactive'}</span>
          )}
          <a href={svc.docsUrl} target="_blank" rel="noreferrer" style={{ fontSize:'0.72rem', color:'var(--gold)', textDecoration:'underline' }}>API Docs</a>
        </div>
      </div>

      <div className="card-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
        {loading ? <div style={{ textAlign:'center', padding:20, color:'var(--text-muted)' }}>Loading…</div> : (
          <>
            {cfg?.configured && (
              <div style={{ padding:'8px 12px', borderRadius:7, background:'var(--bg-base)', border:'1px solid var(--border)', fontSize:'0.8rem', color:'var(--text-muted)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span>Credentials saved — updated {cfg.updated_at ? new Date(cfg.updated_at).toLocaleDateString() : 'unknown'}</span>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div onClick={() => { const n = !active; setActive(n); api(`/admin/integrations/${svc.key}`, { method:'PUT', body: JSON.stringify({ is_active: n }) }).then(load); }}
                    style={{ width:36, height:20, borderRadius:10, position:'relative', cursor:'pointer', background: active ? svc.color : 'var(--border)', transition:'background 0.2s' }}>
                    <div style={{ position:'absolute', top:2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left 0.18s', left: active ? 18 : 2 }} />
                  </div>
                  <button onClick={disconnect} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', fontSize:'0.75rem', fontWeight:600, padding:0, fontFamily:'inherit' }}>Disconnect</button>
                </div>
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {svc.fields.map(f => (
                <div key={f.key}>
                  <label className="form-label">{f.label}</label>
                  <input type={f.type} className="form-input" autoComplete="new-password"
                    placeholder={cfg?.configured ? '(leave blank to keep)' : f.placeholder}
                    value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} />
                </div>
              ))}
            </div>

            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : cfg?.configured ? 'Update' : 'Save & Connect'}
              </button>
              {cfg?.configured && (
                <button className="btn btn-ghost btn-sm" onClick={test} disabled={testing}>
                  {testing ? (
                    <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ display:'inline-block', width:12, height:12, border:'2px solid var(--border)', borderTopColor:'var(--gold)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
                      Testing…
                    </span>
                  ) : 'Test Connection'}
                </button>
              )}
            </div>

            {testRes && (
              <div style={{
                padding:'10px 14px', borderRadius:8,
                background: testRes.ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                border:`1px solid ${testRes.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
              }}>
                <div style={{ fontWeight:700, fontSize:'0.88rem', color: testRes.ok ? 'var(--green)' : 'var(--red)', marginBottom: testRes.message||testRes.error ? 4 : 0 }}>
                  {testRes.ok ? 'Connection Successful' : 'Connection Failed'}
                </div>
                {(testRes.message || testRes.error) && (
                  <div style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>{testRes.ok ? testRes.message : testRes.error}</div>
                )}
              </div>
            )}

            <div style={{ padding:'8px 12px', borderRadius:7, background:'var(--blue-dim)', border:'1px solid rgba(59,130,246,0.25)', fontSize:'0.78rem', color:'var(--blue)', lineHeight:1.5 }}>
              Enable <strong>{svc.name} Integration</strong> on a plan in the Plans page to grant access to those organizations.
            </div>
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => { await confirmAction?.onConfirm(); setConfirmAction(null); }}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel || 'Confirm'}
        danger={confirmAction?.danger !== false}
      />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function IntegrationsPage() {
  return (
    <Layout title="API Integrations">
      <div className="page-content">
        <div style={{ marginBottom:20 }}>
          <h2 style={{ fontFamily:'var(--font-mono)', fontSize:'0.85rem', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:4 }}>
            API Integrations
          </h2>
          <p style={{ fontSize:'0.85rem', color:'var(--text-muted)', maxWidth:640 }}>
            Configure system-wide API credentials for third-party services. Access per organization is controlled by plan feature flags in the Plans page.
          </p>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
          <GoogleMapsCard />
          {SERVICES.map(svc => <ServiceCard key={svc.key} svc={svc} />)}
        </div>
      </div>
    </Layout>
  );
}
